import * as React from 'react';
import KeyboardArrowDownTwoToneIcon from '@mui/icons-material/KeyboardArrowDownTwoTone';
import { Fade, CollapseProps, IconButton, Stack, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { PANEL_PADDING } from '../constants';
import SectionTitle from '@/shared/components/SectionTitle';
import { useEffect, useRef } from 'react';

type Props = {
  title: string | React.ReactNode;
  titleIcon?: React.ReactNode;
  showIcon?: boolean;
  description?: string;
  children: React.ReactNode;
  CollapseProps?: CollapseProps;
  initialExpandedState?: boolean;
  onExpand?: (isCollapsed: boolean) => void;
  toolbar?: React.ReactNode;
};

const ViewingToolsMenuExpandablePanel = React.forwardRef(
  (
    {
      showIcon = true,
      title,
      children,
      description,
      titleIcon,
      CollapseProps = {},
      initialExpandedState = true,
      toolbar,
      onExpand = () => {},
    }: Props,
    _ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [expanded, setExpanded] = React.useState(initialExpandedState);
    const isInitialMount = useRef(true);

    // Sync with parent's initialExpandedState when it changes
    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return; // Skip the initial mount
      }
      setExpanded(initialExpandedState);
    }, [initialExpandedState]);

    const handleToggle = () => {
      const newExpanded = !expanded;
      setExpanded(newExpanded);
      if (onExpand) setTimeout(() => onExpand(newExpanded), 0); // Ensure state is updated before calling onExpand
    };

    return (
      <>
        <Stack
          direction="row"
          sx={{ my: 0, ml: PANEL_PADDING, mr: PANEL_PADDING - 1, py: 1, alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <SectionTitle
              title={title as string}
              padding={0}
              mt={0}
              mb={0}
              description={description as string}
            />
            {titleIcon}
            {toolbar && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {toolbar}
              </Stack>
            )}
          </Stack>
          {showIcon && (
            <IconButton size="small" onClick={handleToggle}>
              <KeyboardArrowDownTwoToneIcon
                sx={(theme: Theme) => ({
                  transition: theme.transitions?.create
                    ? theme.transitions.create(['transform'], {
                        duration: theme.transitions.duration.standard,
                      })
                    : 'transform 0.3s ease', // Fallback transition
                  color: theme.palette.grey[400],
                  transform: expanded ? 'rotate(-180deg)' : 'rotate(0)',
                })}
              />
            </IconButton>
          )}
          
        </Stack>
        <Fade in={expanded}>
          <Fade
            unmountOnExit
            {...CollapseProps}
            sx={{ flexShrink: 0, ...(CollapseProps.sx || {}) }}
            in={expanded}
          >
            <Box sx={{ mt: 0 }}>{children}</Box>
          </Fade>
        </Fade>
      </>
    );
  },
);

export default ViewingToolsMenuExpandablePanel;
